import { renameContact, findContact, contactDisplayName } from '../storage/pairingStore.ts'
import { t, tf } from '../i18n/index.ts'
import { el, escapeHtml } from './dom.ts'

const PROMPT_RENAME_KEY = 'zion_prompt_rename'

export function markPromptNewContactRename(contactId: string): void {
  const id = contactId.trim()
  if (!id) return
  try {
    sessionStorage.setItem(PROMPT_RENAME_KEY, id)
  } catch {
    /* ignore */
  }
}

export function consumePromptNewContactRename(): string {
  try {
    const id = sessionStorage.getItem(PROMPT_RENAME_KEY)?.trim() ?? ''
    if (id) sessionStorage.removeItem(PROMPT_RENAME_KEY)
    return id
  } catch {
    return ''
  }
}

export function peekPromptNewContactRename(): string {
  try {
    return sessionStorage.getItem(PROMPT_RENAME_KEY)?.trim() ?? ''
  } catch {
    return ''
  }
}

/** Modal centrale: conferma invio accoppiamento. */
export function showSendPairingModal(opts: {
  codeLabel?: string
  onConfirm: () => void | Promise<void>
  onCancel?: () => void
}): void {
  document.querySelector('#send-pairing-modal')?.remove()
  const label = (opts.codeLabel ?? '').trim()
  const modal = el(`
    <div class="modal-overlay" id="send-pairing-modal" role="dialog" aria-modal="true">
      <div class="card modal-card">
        <h3>${escapeHtml(t('dialog_send_pairing_title'))}</h3>
        <p class="status muted">${escapeHtml(t('dialog_send_pairing_body'))}</p>
        ${label ? `<p class="status muted small"><strong>${escapeHtml(label)}</strong></p>` : ''}
        <div class="row-btns">
          <button type="button" class="secondary" id="send-pairing-cancel">${escapeHtml(t('chat_rename_cancel'))}</button>
          <button type="button" class="primary big-cta" id="send-pairing-confirm">${escapeHtml(t('dialog_send_pairing_confirm'))}</button>
        </div>
      </div>
    </div>
  `)
  document.body.append(modal)

  const close = () => {
    modal.remove()
    opts.onCancel?.()
  }
  modal.querySelector('#send-pairing-cancel')!.addEventListener('click', close)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close()
  })
  modal.querySelector('#send-pairing-confirm')!.addEventListener('click', () => {
    modal.remove()
    void opts.onConfirm()
  })
}

/**
 * Modal Accetta/Rifiuta per richiesta accoppiamento (foreground push / socket).
 * Persistente fino a scelta utente — evita che la notifica “muoia” senza UX.
 */
export function showIncomingPairRequestModal(opts: {
  nickname?: string
  onAccept: () => void | Promise<void>
  onReject: () => void | Promise<void>
}): void {
  document.querySelector('#incoming-pair-modal')?.remove()
  const name = (opts.nickname ?? 'Utente').trim() || 'Utente'
  const modal = el(`
    <div class="modal-overlay zg-pair-modal" id="incoming-pair-modal" role="dialog" aria-modal="true" aria-labelledby="incoming-pair-title">
      <div class="card modal-card zg-pair-modal-card">
        <h3 id="incoming-pair-title">${escapeHtml(t('home_pair_request_title'))}</h3>
        <p class="status muted">${tf('home_pair_request_body', { name: `<strong>${escapeHtml(name)}</strong>` })}</p>
        <p id="incoming-pair-status" class="status muted small"></p>
        <div class="row-btns">
          <button type="button" class="secondary" id="incoming-pair-reject">${escapeHtml(t('home_pair_reject'))}</button>
          <button type="button" class="primary big-cta" id="incoming-pair-accept">${escapeHtml(t('home_pair_accept'))}</button>
        </div>
      </div>
    </div>
  `)
  document.body.append(modal)

  const statusEl = modal.querySelector('#incoming-pair-status') as HTMLElement
  const acceptBtn = modal.querySelector('#incoming-pair-accept') as HTMLButtonElement
  const rejectBtn = modal.querySelector('#incoming-pair-reject') as HTMLButtonElement

  const close = () => modal.remove()

  rejectBtn.addEventListener('click', () => {
    rejectBtn.disabled = true
    acceptBtn.disabled = true
    void Promise.resolve(opts.onReject()).finally(close)
  })

  acceptBtn.addEventListener('click', () => {
    acceptBtn.disabled = true
    rejectBtn.disabled = true
    acceptBtn.textContent = t('home_pair_accepting')
    statusEl.textContent = t('home_pair_accepting')
    statusEl.className = 'status muted small'
    void Promise.resolve(opts.onAccept())
      .then(() => close())
      .catch((e) => {
        statusEl.textContent = e instanceof Error ? e.message : t('home_pair_accept_failed')
        statusEl.className = 'status err small'
        acceptBtn.disabled = false
        rejectBtn.disabled = false
        acceptBtn.textContent = t('home_pair_accept')
      })
  })
}

/**
 * Popup "Nuovo contatto aggiunto!" — indipendente dalla chat screen
 * (non viene rimosso da clearChatScreenHooks).
 */
export async function showNewContactRenameModal(contactId: string): Promise<void> {
  const id = contactId.trim()
  if (!id) return

  document.querySelector('#new-contact-rename-modal')?.remove()

  const contact = await findContact(id)
  if (!contact) return
  if (contact.customName?.trim()) return

  const nick = contact.nickname?.trim() || contactDisplayName(contact)
  const modal = el(`
    <div class="modal-overlay" id="new-contact-rename-modal" role="dialog" aria-modal="true">
      <form class="card modal-card" id="new-contact-rename-form">
        <h3>${escapeHtml(t('chat_new_contact_title'))}</h3>
        <p class="status muted">${escapeHtml(t('chat_new_contact_body'))}</p>
        ${nick ? `<p class="status muted small">${escapeHtml(tf('chat_rename_nickname', { nick }))}</p>` : ''}
        <input
          id="new-contact-rename-input"
          type="text"
          maxlength="64"
          placeholder="${escapeHtml(t('chat_rename_placeholder'))}"
          autocomplete="off"
        />
        <div class="row-btns">
          <button type="button" class="secondary" id="new-contact-rename-skip">${escapeHtml(t('chat_new_contact_skip'))}</button>
          <button type="submit" class="primary">${escapeHtml(t('chat_rename_save'))}</button>
        </div>
      </form>
    </div>
  `)
  document.body.append(modal)

  const input = modal.querySelector('#new-contact-rename-input') as HTMLInputElement
  const close = () => modal.remove()

  modal.querySelector('#new-contact-rename-skip')!.addEventListener('click', close)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close()
  })
  modal.querySelector('#new-contact-rename-form')!.addEventListener('submit', (e) => {
    e.preventDefault()
    void (async () => {
      await renameContact(id, input.value)
      close()
      // Aggiorna titolo chat se aperta
      const title = document.querySelector('#contact-title')
      const updated = await findContact(id)
      if (title && updated) {
        title.textContent = contactDisplayName(updated)
      }
    })()
  })

  window.setTimeout(() => input.focus(), 40)
}

/** Dopo navigate('chat'): apri il rename se marcato. */
export function scheduleNewContactRenamePrompt(contactId?: string): void {
  const id = (contactId ?? peekPromptNewContactRename()).trim()
  if (!id) return
  window.setTimeout(() => {
    const pending = consumePromptNewContactRename()
    const target = (pending || id).trim()
    if (!target) return
    void showNewContactRenameModal(target)
  }, 350)
}
